import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const DRAWER_OPEN_EVENT = 'dialog:open';
const MAX_RETRIES = 2;

class CartDrawerRecommendations extends HTMLElement {
  #productId = null;
  #cache = {};
  #abortController = null;
  #retryCount = 0;
  #loaded = false;
  #loading = false;
  #cartDrawer = null;

  #mutationObserver = new MutationObserver(() => {
    const newId = this.dataset.productId;
    if (newId !== this.#productId) {
      this.#productId = newId || null;
      this.#loaded = false;
      this.#retryCount = 0;
      if (this.#isDrawerOpen()) this.#handleDrawerOpen();
    }
  });

  #handleDrawerOpen = () => {
    if (this.#needsLoad()) {
      this.#load();
    }
  };

  /**
   * Any cart change re-renders the drawer section, which wipes the rendered list. The product
   * id often stays the same (removing a item that is not the first one), so an attribute diff
   * is not enough to notice — react to the cart itself.
   */
  #handleCartUpdate = () => {
    this.#loaded = false;
    this.#retryCount = 0;
    if (this.#isDrawerOpen()) this.#handleDrawerOpen();
  };

  /**
   * Whether the drawer this element lives in is currently open.
   * @returns {boolean}
   */
  #isDrawerOpen() {
    return Boolean(this.#cartDrawer?.querySelector('dialog')?.open);
  }

  /**
   * Decided from the DOM rather than from #loaded alone: a re-render can empty the list while
   * the element instance survives with its flags intact, which is what left the heading
   * stranded above nothing.
   * @returns {boolean}
   */
  #needsLoad() {
    if (!this.#productId || this.#loading) return false;
    const list = this.querySelector('[data-recommendations-list]');
    return !this.#loaded || !list?.children.length;
  }

  connectedCallback() {
    this.#productId = this.dataset.productId || null;
    this.#mutationObserver.observe(this, { attributes: true, attributeFilter: ['data-product-id'] });

    // dialog:open fires on cart-drawer-component without bubbling — listen there directly
    this.#cartDrawer = this.closest('cart-drawer-component');
    this.#cartDrawer?.addEventListener(DRAWER_OPEN_EVENT, this.#handleDrawerOpen);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Adding to cart opens the drawer and re-renders the cart section. When this element is
    // recreated by that render, dialog:open has already fired and would never fire again —
    // leaving the heading sitting above an empty list. Load straight away in that case.
    if (this.#isDrawerOpen()) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#abortController?.abort();
    this.#cartDrawer?.removeEventListener(DRAWER_OPEN_EVENT, this.#handleDrawerOpen);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#cartDrawer = null;
  }

  async #load() {
    const productId = this.#productId;
    if (!productId || this.#loading) return;

    this.#loading = true;

    const limit = this.dataset.limit || '2';
    const intent = this.dataset.intent || 'related';
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'BRL';

    const products = await this.#fetchWithFallback(productId, limit, intent);

    this.#loading = false;

    if (products === null) {
      if (this.#retryCount < MAX_RETRIES) {
        this.#retryCount++;
        setTimeout(() => this.#load(), 1000 * this.#retryCount);
      } else {
        this.hidden = true;
      }
      return;
    }

    this.#retryCount = 0;
    this.#loaded = true;
    this.#render(products, moneyFormat, currency);
  }

  async #fetchWithFallback(productId, limit, intent) {
    let products = await this.#fetchProducts(productId, limit, intent);

    if (products !== null && products.length === 0 && intent === 'complementary') {
      products = await this.#fetchProducts(productId, limit, 'related');
    }

    return products;
  }

  async #fetchProducts(productId, limit, intent) {
    const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

    if (this.#cache[url]) return this.#cache[url];

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return null;
      const data = await response.json();
      const result = data.products ?? [];
      this.#cache[url] = result;
      return result;
    } catch (e) {
      return e.name === 'AbortError' ? [] : null;
    }
  }

  #render(products, moneyFormat, currency) {
    const list = this.querySelector('[data-recommendations-list]');
    if (!list) return;

    if (!products?.length) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    list.innerHTML = products
      .map((product) => {
        const src = product.featured_image || product.images?.[0]?.src;
        const imgHtml = src
          ? `<div class="cart-drawer__recommendation-image-wrapper">
              <img src="${src}${src.includes('?') ? '&' : '?'}width=200" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" class="cart-drawer__recommendation-image">
            </div>`
          : '';
        const cents = product.price_min ?? product.price ?? 0;
        const price = formatMoney(cents, moneyFormat, currency);

        return `<a href="${product.url}" class="cart-drawer__recommendation-card">
          ${imgHtml}
          <div class="cart-drawer__recommendation-info">
            <span class="cart-drawer__recommendation-title">${product.title}</span>
            <span class="cart-drawer__recommendation-price">${price}</span>
          </div>
        </a>`;
      })
      .join('');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}